import { ShoppingBag, Heart, Star, Plus, Minus, ShoppingCart, Layers } from "lucide-react";

const TYPE_LABELS = { digital: "Digital", physical: "Physical" };

/**
 * Product card used in the marketplace home grid.
 * Extracted from MarketplaceHome.jsx (Session 30, PD-2).
 *
 * Session 36: optional wishlist heart (onToggleSave + saved).
 * Session 37: optional displayTitle for translated product names.
 * Session 62d: star rating (hidden until a product has a review, his call) and add-to-cart in
 *   place — every purchase used to need a trip into the product page and back out again.
 */
export function ProductCard({
  product, onClick, saved = false, onToggleSave, displayTitle,
  cartQty = 0, onAdd, onQty,
}) {
  const rating = Number(product.rating_avg || 0);
  const reviews = Number(product.rating_count || 0);
  const needsChoice = (product.variants?.length || 0) > 0;
  const stop = (e) => { e.stopPropagation(); };

  return (
    <div className="mp-product-card" onClick={onClick} data-testid={`product-card-${product.id}`}>
      <div className="mp-product-img">
        {product.images?.length > 0 ? (
          <img
            src={product.images[0]}
            alt={product.title}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <ShoppingBag size={28} />
        )}
        {onToggleSave && (
          <button
            type="button"
            className={`mp-save-btn${saved ? " is-saved" : ""}`}
            data-testid={`wishlist-toggle-${product.id}`}
            aria-label={saved ? "Remove from saved" : "Save for later"}
            title={saved ? "Saved — click to remove" : "Save for later"}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSave(product);
            }}
          >
            <Heart size={16} fill={saved ? "currentColor" : "none"} />
          </button>
        )}
      </div>
      <div className="mp-product-body">
        <p className="mp-product-type">{TYPE_LABELS[product.type]}</p>
        <p className="mp-product-name">{displayTitle || product.title}</p>
        {/* The slot is always here, empty or not, so the price and button stay on the same
            line across a row — a rating on one card used to shove its neighbours out of step. */}
        <div className="mp-card-rating" data-testid={reviews > 0 ? `product-rating-${product.id}` : undefined}>
          {reviews > 0 && (
            <>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={11} fill={n <= Math.round(rating) ? "currentColor" : "none"} />
              ))}
              <span>{rating.toFixed(1)} ({reviews})</span>
            </>
          )}
        </div>
        <p className="mp-product-price">${Number(product.price).toFixed(2)}</p>

        {/* Sold in types — the buyer picks on the product page. Adding from here used to
            drop the base item in the basket with no type at all (owner, Jun 2026). */}
        {needsChoice && (
          <button type="button" className="mp-card-add"
            onClick={(e) => { stop(e); onClick?.(); }}
            data-testid={`choose-type-${product.id}`}>
            <Layers size={13} /> Choose type
          </button>
        )}

        {onAdd && !needsChoice && cartQty === 0 && (
          <button type="button" className="mp-card-add"
            onClick={(e) => { stop(e); onAdd(product); }}
            data-testid={`add-to-cart-${product.id}`}>
            <ShoppingCart size={13} /> Add to cart
          </button>
        )}
        {onQty && !needsChoice && cartQty > 0 && (
          <div className="mp-card-qty" onClick={stop} data-testid={`cart-qty-${product.id}`}>
            <button type="button" onClick={(e) => { stop(e); onQty(product, cartQty - 1); }}
              data-testid={`cart-qty-minus-${product.id}`} aria-label="One fewer">
              <Minus size={12} />
            </button>
            <span className="mp-card-qty-val">{cartQty} in cart</span>
            <button type="button" onClick={(e) => { stop(e); onQty(product, cartQty + 1); }}
              data-testid={`cart-qty-plus-${product.id}`} aria-label="One more">
              <Plus size={12} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
